/* eslint-disable */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  STORAGE,
  loadDecoys,
  loadTrash,
  loadCustomFolders,
  addCustomFolder,
  removeCustomFolder,
  t,
} from "@/lib/mr";

/**
 * Apple Notes-style Folders screen. Renders an "iCloud" section with:
 *   • Notes                 → /notes (the main disguise)
 *   • Recently Deleted      → /recently-deleted (the trash bin)
 *   • …plus any spectator-created custom folders (persisted in
 *     localStorage under STORAGE.DECOY_FOLDERS). Custom folders open
 *     into a stub "No Notes" view and can be swipe-deleted.
 *
 * A bottom bar exposes a "New Folder" affordance that fires an
 * iOS-style alert dialog for entering a name. This is purely
 * decorative — the created folders never contain real notes — but it
 * silences the biggest "tell" a curious spectator would notice: the
 * inability to create folders like they can on their own iPhone.
 *
 * Magician exit — triple-tapping the "Folders" title (or anywhere in
 * the top 60 px of the screen) fast-tracks back to /setup.
 */
export default function Folders() {
  const navigate = useNavigate();
  const langCode = localStorage.getItem(STORAGE.LANGUAGE) || "auto";
  const isRTL = /^(?:ar|he|fa|ur)/.test(langCode || "");
  const decoys = useMemo(() => loadDecoys(), []);
  const trash = useMemo(() => loadTrash(), []);
  const [customFolders, setCustomFolders] = useState(() => loadCustomFolders());
  const notesCount = decoys.length;
  const trashCount = trash.length;
  // Cosmetic iCloud "storage almost full" banner (matches DecoyNotes).
  const iCloudBannerEnabled = (() => {
    const v = localStorage.getItem(STORAGE.ICLOUD_BANNER_ENABLED);
    return v === null ? true : v === "1";
  })();

  // Triple-tap escape hatch — mirrors DecoyNotes' onHeaderTap.
  const taps = useRef({ list: [] });
  const onTitleTap = () => {
    const now = Date.now();
    taps.current.list = [...taps.current.list, now].filter((t) => now - t < 700);
    if (taps.current.list.length >= 3) {
      taps.current.list = [];
      navigate("/");
    }
  };

  // ─── New Folder dialog ─────────────────────────────────────────
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const inputRef = useRef(null);
  useEffect(() => {
    if (showNewDialog) {
      // Wait a frame so the input exists before focussing.
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [showNewDialog]);
  const commitNewFolder = () => {
    const trimmed = newFolderName.trim();
    if (!trimmed) {
      setShowNewDialog(false);
      setNewFolderName("");
      return;
    }
    const next = addCustomFolder(trimmed);
    setCustomFolders(next);
    setShowNewDialog(false);
    setNewFolderName("");
  };
  const cancelNewFolder = () => {
    setShowNewDialog(false);
    setNewFolderName("");
  };

  // ─── Swipe on custom folder rows → reveals Delete ──────────────
  const rowRefs = useRef(new Map());
  const [swipedFolderId, setSwipedFolderId] = useState(null);
  const swipeState = useRef({
    id: null,
    dragging: false,
    startX: 0,
    startY: 0,
    baseDx: 0,
    dx: 0,
    direction: null,
  });
  const SWIPE_REVEAL = 84;
  const SWIPE_COMMIT = 200;
  const setRowTransform = (rowId, dx) => {
    const el = rowRefs.current.get(rowId);
    if (el) el.style.transform = `translate3d(${dx}px,0,0)`;
  };
  const snapRow = (rowId, target) => {
    const el = rowRefs.current.get(rowId);
    if (!el) return;
    el.style.transition = "transform 220ms cubic-bezier(0.22, 1, 0.36, 1)";
    el.style.transform = `translate3d(${target}px,0,0)`;
    window.setTimeout(() => {
      if (el) el.style.transition = "";
    }, 240);
  };
  const closeAnySwipedFolder = useCallback(() => {
    if (!swipedFolderId) return;
    snapRow(swipedFolderId, 0);
    setSwipedFolderId(null);
  }, [swipedFolderId]);
  const onFolderPointerDown = (rowId, clientX, clientY) => {
    if (swipedFolderId && swipedFolderId !== rowId) closeAnySwipedFolder();
    const s = swipeState.current;
    s.id = rowId;
    s.dragging = true;
    s.startX = clientX;
    s.startY = clientY;
    s.baseDx = swipedFolderId === rowId ? -SWIPE_REVEAL : 0;
    s.dx = s.baseDx;
    s.direction = null;
  };
  const onFolderPointerMove = (rowId, clientX, clientY) => {
    const s = swipeState.current;
    if (!s.dragging || s.id !== rowId) return;
    const dx = clientX - s.startX;
    const dy = clientY - s.startY;
    if (!s.direction) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      s.direction = Math.abs(dx) > Math.abs(dy) ? "h" : "v";
      if (s.direction === "v") {
        s.dragging = false;
        return;
      }
    }
    let nextDx = (s.baseDx || 0) + dx;
    if (nextDx > 0) nextDx = 0;
    if (nextDx < -240) nextDx = -240;
    s.dx = nextDx;
    setRowTransform(rowId, nextDx);
  };
  const onFolderPointerUp = (rowId) => {
    const s = swipeState.current;
    if (!s.dragging || s.id !== rowId) return;
    s.dragging = false;
    if (s.dx <= -SWIPE_COMMIT) {
      handleDeleteFolder(rowId);
      return;
    }
    if (s.dx <= -SWIPE_REVEAL / 2) {
      snapRow(rowId, -SWIPE_REVEAL);
      setSwipedFolderId(rowId);
    } else {
      snapRow(rowId, 0);
      setSwipedFolderId((prev) => (prev === rowId ? null : prev));
    }
  };
  const handleDeleteFolder = (id) => {
    const next = removeCustomFolder(id);
    rowRefs.current.delete(id);
    setSwipedFolderId(null);
    setCustomFolders(next);
  };
  const handleOpenCustomFolder = (id) => {
    if (swipedFolderId) {
      // If any row is already peeled open, a tap should close it
      // instead of navigating — matches iOS.
      closeAnySwipedFolder();
      return;
    }
    navigate(`/folders/${id}`);
  };

  return (
    <div
      className="fixed inset-0 w-screen h-screen bg-[#F2F2F7] text-black flex flex-col"
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Helvetica Neue", Arial, sans-serif',
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
      dir={isRTL ? "rtl" : "ltr"}
      onContextMenu={(e) => e.preventDefault()}
      onClick={closeAnySwipedFolder}
      data-testid="folders-page"
    >
      {/* Header (pill buttons + big title + iCloud banner) moved INSIDE
          the scroll container as a `sticky top-0` element so the
          folders list literally scrolls behind the frosted header —
          native iOS Notes 2024/2025 behaviour. Search bar has moved
          to the fixed bottom bar (also matching native). */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 pb-40">
        {/* ---------- Sticky frosted header ---------- */}
        <div
          className="sticky top-0 z-20 -mx-4 backdrop-blur-xl backdrop-saturate-150 bg-[#F2F2F7]/75"
          data-testid="folders-sticky-header"
        >
          {/* Top pill-button row */}
          <div className="px-4 pt-2 pb-1 flex items-center justify-end gap-2 select-none">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowNewDialog(true);
              }}
              className="w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-[0_0.5px_2px_rgba(0,0,0,0.08)] active:bg-neutral-100 transition-colors border-0 p-0"
              data-testid="folders-new-folder-top"
              aria-label="new folder"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M3 6.5A1.5 1.5 0 0 1 4.5 5h4.086a1.5 1.5 0 0 1 1.06.44l1.415 1.414A1.5 1.5 0 0 0 12.121 7.5H19.5A1.5 1.5 0 0 1 21 9v8.5A1.5 1.5 0 0 1 19.5 19h-15A1.5 1.5 0 0 1 3 17.5v-11z"
                  stroke="#000"
                  strokeWidth="1.7"
                />
                <path
                  d="M12 11v6M9 14h6"
                  stroke="#000"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            <button
              type="button"
              className="h-9 px-4 rounded-full bg-white flex items-center justify-center shadow-[0_0.5px_2px_rgba(0,0,0,0.08)] active:bg-neutral-100 transition-colors border-0 text-[15px] text-black font-normal"
              data-testid="folders-edit"
              aria-label="edit"
              onClick={(e) => e.preventDefault()}
            >
              {langCode.startsWith("nl") ? "Wijzig" : "Edit"}
            </button>
          </div>

          {/* Big page title + iCloud banner */}
          <div className="px-5 pt-3 pb-3">
            <h1
              className="text-[34px] font-bold leading-tight text-black"
              data-testid="folders-title"
              onClick={onTitleTap}
            >
              {t("folders", langCode)}
            </h1>
            {iCloudBannerEnabled && (
              <p
                className="mt-1 flex items-center gap-1.5 text-[15px] text-neutral-500 select-none"
                data-testid="icloud-storage-banner-folders"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                  className="shrink-0"
                >
                  <path
                    d="M7.3 17.5h8.4a4 4 0 0 0 .6-7.94 5.5 5.5 0 0 0-10.86-.9A3.9 3.9 0 0 0 7.3 17.5Z"
                    fill="#8e8e93"
                  />
                  <circle cx="17.4" cy="17.4" r="3.6" fill="#8e8e93" stroke="#f2f2f2" strokeWidth="0.9" />
                  <path d="M17.4 15.55v2.05" stroke="#f2f2f2" strokeWidth="1.05" strokeLinecap="round" />
                  <circle cx="17.4" cy="19.1" r="0.55" fill="#f2f2f2" />
                </svg>
                <span>
                  {t("icloud_full", langCode)}
                  <span aria-hidden="true"> · </span>
                  <span style={{ color: "#FFB400" }}>
                    {t("icloud_get_more", langCode)}
                  </span>
                </span>
              </p>
            )}
          </div>
        </div>

        <h2
          className="text-[22px] font-bold text-black leading-tight px-1 mb-2 flex items-center justify-between"
          data-testid="folders-section-icloud"
        >
          <span>{t("icloud", langCode)}</span>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="shrink-0">
            <path d="M6 9l6 6 6-6" stroke="#FFB400" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </h2>
        <ul className="bg-white rounded-[12px] overflow-hidden divide-y divide-neutral-200/80 shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
          {/* Notes folder → main decoy view */}
          <li>
            <button
              type="button"
              onClick={() => navigate("/notes")}
              className="w-full flex items-center gap-3 px-4 py-3 text-left active:bg-neutral-100 transition-colors"
              data-testid="folders-row-notes"
            >
              <FolderIcon />
              <span className="flex-1 text-[17px] text-black leading-tight truncate">
                {t("notes", langCode)}
              </span>
              <span className="text-[15px] text-neutral-500 tabular-nums" data-testid="folders-count-notes">
                {notesCount}
              </span>
              <ChevronRight />
            </button>
          </li>

          {/* Custom folders — swipe-left → Delete */}
          {customFolders.map((f) => (
            <CustomFolderRow
              key={f.id}
              folder={f}
              langCode={langCode}
              isRTL={isRTL}
              registerEl={(el) => {
                if (el) rowRefs.current.set(f.id, el);
                else rowRefs.current.delete(f.id);
              }}
              onPointerDown={onFolderPointerDown}
              onPointerMove={onFolderPointerMove}
              onPointerUp={onFolderPointerUp}
              onDelete={handleDeleteFolder}
              onOpen={handleOpenCustomFolder}
              isSwiped={swipedFolderId === f.id}
              onTapSwiped={closeAnySwipedFolder}
            />
          ))}

          {/* Recently Deleted → trash bin */}
          <li>
            <button
              type="button"
              onClick={() => navigate("/recently-deleted")}
              className="w-full flex items-center gap-3 px-4 py-3 text-left active:bg-neutral-100 transition-colors"
              data-testid="folders-row-trash"
            >
              <TrashIcon />
              <span className="flex-1 text-[17px] text-black leading-tight truncate">
                {t("recently_deleted", langCode)}
              </span>
              <span className="text-[15px] text-neutral-500 tabular-nums" data-testid="folders-count-trash">
                {trashCount}
              </span>
              <ChevronRight />
            </button>
          </li>
        </ul>

        {/* On My iPhone section. Real iOS Notes shows a second account
            header for any user with the "On My iPhone" account enabled
            in Settings → Notes. Rendering it here (even with a single
            empty Notes folder) closes a subtle disguise gap: spectators
            familiar with iOS immediately register the two-account
            hierarchy as authentic. Recently Deleted intentionally lives
            ONLY under iCloud — mirrors the current default iOS layout
            where the local trash folder is hidden until at least one
            local note is deleted. */}
        <h2
          className="text-[22px] font-bold text-black leading-tight px-1 mt-7 mb-2 flex items-center justify-between"
          data-testid="folders-section-onmyiphone"
        >
          <span>{t("on_my_iphone", langCode)}</span>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="shrink-0">
            <path d="M6 9l6 6 6-6" stroke="#FFB400" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </h2>
        <ul className="bg-white rounded-[12px] overflow-hidden divide-y divide-neutral-200/80 shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
          <li>
            <button
              type="button"
              onClick={() => navigate("/notes")}
              className="w-full flex items-center gap-3 px-4 py-3 text-left active:bg-neutral-100 transition-colors"
              data-testid="folders-row-notes-local"
            >
              <FolderIcon />
              <span className="flex-1 text-[17px] text-black leading-tight truncate">
                {t("notes", langCode)}
              </span>
              <span
                className="text-[15px] text-neutral-500 tabular-nums"
                data-testid="folders-count-notes-local"
              >
                0
              </span>
              <ChevronRight />
            </button>
          </li>
        </ul>
      </div>

      {/* Fixed bottom bar — frosted-glass search pill + rounded-square
          compose button, mirroring native iOS Notes 2024/2025 Folders
          layout. The compose icon opens the "new folder" dialog (still
          the same primary CTA for this screen). Search is decorative. */}
      <div
        className="absolute bottom-0 inset-x-0 z-40 px-4 pt-2 pb-3 flex items-center gap-2 bg-[#F2F2F7]/70 backdrop-blur-xl backdrop-saturate-150"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)" }}
        data-testid="folders-bottom-bar"
      >
        <div
          className="flex-1 flex items-center gap-2 bg-neutral-200/80 rounded-[10px] px-3 py-2 backdrop-blur"
          data-testid="folders-search-bar"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="11" cy="11" r="7" stroke="#8e8e93" strokeWidth="2" />
            <path d="M20 20l-3.2-3.2" stroke="#8e8e93" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            placeholder={t("search", langCode)}
            aria-label={t("search", langCode)}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck="false"
            className="flex-1 bg-transparent border-0 outline-none text-[15px] text-black placeholder:text-neutral-500 leading-none p-0 min-w-0"
            data-testid="folders-search-input"
          />
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="#8e8e93"
            aria-hidden="true"
          >
            <path d="M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3z"/>
            <path d="M18 12a6 6 0 0 1-12 0H4.5a7.5 7.5 0 0 0 6.75 7.46V22h1.5v-2.54A7.5 7.5 0 0 0 19.5 12H18z"/>
          </svg>
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setShowNewDialog(true);
          }}
          className="shrink-0 w-11 h-11 rounded-[12px] bg-white flex items-center justify-center shadow-[0_0.5px_2px_rgba(0,0,0,0.08)] active:bg-neutral-100 transition-colors border-0 p-0"
          aria-label={t("new_folder", langCode)}
          data-testid="folders-new-folder-btn"
        >
          <svg width="21" height="21" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M15.5 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8.5"
              stroke="#000"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M18.586 2.586a2 2 0 0 1 2.828 2.828L12 14.828 8 16l1.172-4 9.414-9.414z"
              stroke="#000"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* iOS-style alert dialog for naming a new folder. Blocks the
          whole viewport until the user commits/cancels. */}
      {showNewDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-8"
          data-testid="folders-new-folder-dialog"
          onClick={(e) => {
            e.stopPropagation();
            cancelNewFolder();
          }}
        >
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative w-full max-w-[280px] bg-white/95 backdrop-blur rounded-[14px] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 pt-4 pb-3 text-center">
              <p className="text-[17px] font-semibold text-black leading-tight">
                {t("new_folder_title", langCode)}
              </p>
              <p className="text-[13px] text-neutral-700 mt-2 leading-snug">
                {t("new_folder_body", langCode)}
              </p>
              <input
                ref={inputRef}
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitNewFolder();
                  if (e.key === "Escape") cancelNewFolder();
                }}
                placeholder={t("new_folder_placeholder", langCode)}
                className="mt-3 w-full text-[14px] leading-tight bg-white border border-neutral-300 rounded-[6px] px-2 py-1.5 outline-none focus:border-[#FFB400]"
                data-testid="folders-new-folder-input"
                maxLength={60}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="sentences"
                spellCheck="false"
              />
            </div>
            <div className="flex border-t border-neutral-300/70">
              <button
                type="button"
                onClick={cancelNewFolder}
                className="flex-1 py-2.5 text-[17px] text-[#FFB400] bg-transparent border-0"
                data-testid="folders-new-folder-cancel"
              >
                {t("cancel", langCode)}
              </button>
              <div className="w-px bg-neutral-300/70" />
              <button
                type="button"
                onClick={commitNewFolder}
                disabled={!newFolderName.trim()}
                className={`flex-1 py-2.5 text-[17px] font-semibold bg-transparent border-0 ${
                  newFolderName.trim() ? "text-[#FFB400]" : "text-neutral-300"
                }`}
                data-testid="folders-new-folder-save"
              >
                {t("save", langCode)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// A custom folder row with swipe-to-delete support.
function CustomFolderRow({
  folder,
  langCode,
  isRTL,
  registerEl,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onDelete,
  onOpen,
  isSwiped,
  onTapSwiped,
}) {
  return (
    <li className="relative overflow-hidden bg-[#F2F2F7]">
      {/* Red delete button revealed by swipe-left. */}
      <div className={`absolute inset-y-0 ${isRTL ? "left-0" : "right-0"} flex`}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(folder.id);
          }}
          className="w-[84px] h-full bg-[#FF3B30] text-white text-[15px] font-semibold flex items-center justify-center"
          data-testid={`folders-custom-delete-${folder.id}`}
        >
          {t("delete_action", langCode)}
        </button>
      </div>
      <div
        ref={registerEl}
        className="relative bg-white will-change-transform"
        style={{ touchAction: "pan-y" }}
        onTouchStart={(e) => {
          const t0 = e.touches[0];
          onPointerDown(folder.id, t0.clientX, t0.clientY);
        }}
        onTouchMove={(e) => {
          const t0 = e.touches[0];
          onPointerMove(folder.id, t0.clientX, t0.clientY);
        }}
        onTouchEnd={() => onPointerUp(folder.id)}
        onTouchCancel={() => onPointerUp(folder.id)}
        onMouseDown={(e) => onPointerDown(folder.id, e.clientX, e.clientY)}
        onMouseMove={(e) => {
          if (e.buttons === 1) onPointerMove(folder.id, e.clientX, e.clientY);
        }}
        onMouseUp={() => onPointerUp(folder.id)}
        onMouseLeave={() => onPointerUp(folder.id)}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (isSwiped) {
              onTapSwiped();
              return;
            }
            onOpen(folder.id);
          }}
          className="w-full flex items-center gap-3 px-4 py-3 text-left active:bg-neutral-100 transition-colors"
          data-testid={`folders-custom-row-${folder.id}`}
        >
          <FolderIcon />
          <span className="flex-1 text-[17px] text-black leading-tight truncate">
            {folder.name}
          </span>
          <span className="text-[15px] text-neutral-500 tabular-nums">0</span>
          <ChevronRight />
        </button>
      </div>
    </li>
  );
}

// iOS Notes uses OUTLINED yellow folder + trash glyphs on the folders
// screen — thin strokes with clear space inside. Rendered inline so we
// don't drag in an icon library just for two shapes.
function FolderIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" data-testid="folders-icon-notes">
      <path
        d="M3 6.5A1.5 1.5 0 0 1 4.5 5h4.086a1.5 1.5 0 0 1 1.06.44l1.415 1.414A1.5 1.5 0 0 0 12.121 7.5H19.5A1.5 1.5 0 0 1 21 9v8.5A1.5 1.5 0 0 1 19.5 19h-15A1.5 1.5 0 0 1 3 17.5v-11z"
        stroke="#FFB400"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" data-testid="folders-icon-trash">
      {/* lid handle */}
      <path d="M10 4.25h4" stroke="#FFB400" strokeWidth="1.6" strokeLinecap="round" />
      {/* top rim */}
      <path d="M4 7h16" stroke="#FFB400" strokeWidth="1.6" strokeLinecap="round" />
      {/* can body */}
      <path
        d="M6 7l0.9 12.35A2 2 0 0 0 8.9 21h6.2a2 2 0 0 0 2-1.65L18 7"
        stroke="#FFB400"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* vertical strokes inside */}
      <path d="M10 10.5v6M14 10.5v6" stroke="#FFB400" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg width="10" height="16" viewBox="0 0 10 16" fill="none" aria-hidden="true">
      <path d="M1 1l7 7-7 7" stroke="#C7C7CC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
